import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type ColumnKey =
  | 'name' // Always visible (sticky)
  | 'basic'
  | 'da'
  | 'hra'
  | 'travel'
  | 'conv'
  | 'medical'
  | 'special'
  | 'bonus'
  | 'other'
  | 'gross'
  | 'earningTillToday'
  | 'workingDaysEarning'
  | 'otEarning'
  | 'paidLeaveEarning'
  | 'sandwichDeduction'
  | 'absent'
  | 'adv'
  | 'pTax'
  | 'tds'
  | 'epf'
  | 'nps'
  | 'esi'
  | 'otherDeduction'
  | 'total'
  | 'netPay'
  | 'advanceOpen'
  | 'advanceAdd'
  | 'advanceDedu'
  | 'advanceBal'
  | 'tl'
  | 'olAvailable'
  | 'slTaken'
  | 'clTaken'
  | 'elTaken'
  | 'olTaken'
  | 'absentDays'
  | 'absentAmount';

interface SalaryTableState {
  // Column visibility state - all columns visible by default except name (always visible)
  columnVisibility: Record<ColumnKey, boolean>;
  
  // Actions
  toggleColumn: (column: ColumnKey) => void;
  setColumnVisibility: (column: ColumnKey, visible: boolean) => void;
  resetColumns: () => void;
}

const defaultVisibility: Record<ColumnKey, boolean> = {
  name: true, // Always visible
  basic: true,
  da: true,
  hra: true,
  travel: true,
  conv: true,
  medical: true,
  special: true,
  bonus: true,
  other: true,
  gross: true,
  earningTillToday: true,
  workingDaysEarning: true,
  otEarning: true,
  paidLeaveEarning: true,
  sandwichDeduction: true,
  absent: true,
  adv: true,
  pTax: true,
  tds: true,
  epf: true,
  nps: true,
  esi: true,
  otherDeduction: true,
  total: true,
  netPay: true,
  advanceOpen: true,
  advanceAdd: true,
  advanceDedu: true,
  advanceBal: true,
  tl: true,
  olAvailable: true,
  slTaken: true,
  clTaken: true,
  elTaken: true,
  olTaken: true,
  absentDays: true,
  absentAmount: true,
};

/**
 * Zustand store for salary table column visibility
 * Automatically persists to localStorage using Zustand's persist middleware
 */
export const useSalaryTableStore = create<SalaryTableState>()(
  persist(
    (set) => ({
      // Initial state - all columns visible
      columnVisibility: defaultVisibility,

      // Actions
      toggleColumn: (column) =>
        set((state) => {
          // Prevent hiding the name column
          if (column === 'name') return state;
          
          return {
            columnVisibility: {
              ...state.columnVisibility,
              [column]: !state.columnVisibility[column],
            },
          };
        }),

      setColumnVisibility: (column, visible) =>
        set((state) => {
          // Prevent hiding the name column
          if (column === 'name' && !visible) return state;
          
          return {
            columnVisibility: {
              ...state.columnVisibility,
              [column]: visible,
            },
          };
        }),

      resetColumns: () =>
        set({
          columnVisibility: defaultVisibility,
        }),
    }),
    {
      name: 'salary-table-storage', // localStorage key
      storage: createJSONStorage(() => localStorage),
      merge: (persistedState: unknown, currentState: SalaryTableState) => {
        const persisted = persistedState as Partial<SalaryTableState> | undefined;
        return {
          ...currentState,
          columnVisibility: {
            ...defaultVisibility,
            ...(persisted?.columnVisibility || {}),
          },
        };
      },
    }
  )
);

